import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaStar } from 'react-icons/fa';
import {
  getTenantScoreByUserId,
  getTenantScoreByUserIdAsAgent,
  getMyProperties,
  getPropertyUnitRequests,
  getPropertyTenancyHistory,
  getManageBnbBookings,
} from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import {
  writeAccessStatus,
  getTenantUserIdFromUnitRequest,
} from '../../../utils/tenantScoreAccess';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import './TenantScore.css';

/** Unit-request workflow statuses to include on Tenant scores (uses GET .../unit-requests?status=). */
const TENANT_SCORE_UNIT_REQUEST_STATUSES = ['REQUESTED', 'APPROVED', 'ASSIGNED'];

async function fetchActiveUnitRequestsForProperty(propertyId) {
  const lists = await Promise.all(
    TENANT_SCORE_UNIT_REQUEST_STATUSES.map((st) =>
      getPropertyUnitRequests(propertyId, { status: st })
        .then((res) => (Array.isArray(res?.data) ? res.data : []))
        .catch(() => [])
    )
  );
  const seenIds = new Set();
  const merged = [];
  for (const list of lists) {
    for (const req of list) {
      const id = req?.id;
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      merged.push({ ...req, __sourcePropertyId: propertyId });
    }
  }
  return merged;
}

async function fetchManualTenanciesForProperty(propertyId) {
  return getPropertyTenancyHistory(propertyId)
    .then((res) => (Array.isArray(res?.data) ? res.data : []))
    .then((rows) =>
      rows.map((row) => ({
        ...row,
        status: 'MANUAL',
        __sourcePropertyId: propertyId,
      }))
    )
    .catch(() => []);
}

/** BnB booking statuses to include for Tenant scores (sourced via GET /api/bnb/bookings/manage). */
const TENANT_SCORE_BNB_BOOKING_STATUSES = ['REQUESTED', 'APPROVED', 'CONFIRMED', 'ASSIGNED'];

async function fetchRelevantBnbBookingsForProperty(propertyId) {
  const lists = await Promise.all(
    TENANT_SCORE_BNB_BOOKING_STATUSES.map((st) =>
      getManageBnbBookings(propertyId, st)
        .then((res) => (Array.isArray(res?.data) ? res.data : []))
        .catch(() => [])
    )
  );
  const seenIds = new Set();
  const merged = [];
  for (const list of lists) {
    for (const b of list) {
      const id = b?.id;
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      merged.push({ ...b, __sourcePropertyId: propertyId });
    }
  }
  return merged;
}



const TenantScore = ({ variant = 'landlord', onNotify, navigationState = {} }) => {
  const [unitApplicants, setUnitApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [applicantsError, setApplicantsError] = useState('');
  const [tenantCodeFilter, setTenantCodeFilter] = useState('');
  const [monthsBack, setMonthsBack] = useState(12);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [viewedScore, setViewedScore] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreError, setScoreError] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('applicants'); // 'applicants' | 'ratings'
  const [ratingsTenantFilter, setRatingsTenantFilter] = useState(null); // { userId, name, ratings }
  const [ratingsPage, setRatingsPage] = useState(1);
  const ratingsPerPage = 20;

  // Initialize from navigation state if present (e.g. from unit requests "View all ratings")
  useEffect(() => {
    if (navigationState?.ratingsTenantFilter) {
      setRatingsTenantFilter(navigationState.ratingsTenantFilter);
      setActiveTab('ratings');
    }
  }, [navigationState]);

  // bumpAccessMap removed

  // reconcileRequestedAccessFromScoreApi and syncOutgoingScoreRequests removed

  const formatPeriod = (payload) => {
    const fromMonth = payload?.fromMonth;
    const fromYear = payload?.fromYear;
    const toMonth = payload?.toMonth;
    const toYear = payload?.toYear;
    if (!fromMonth || !fromYear || !toMonth || !toYear) return '—';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[fromMonth - 1] || fromMonth} ${fromYear} - ${monthNames[toMonth - 1] || toMonth} ${toYear}`;
  };

  const getScoreBand = (score) => {
    const n = Number(score);
    if (!Number.isFinite(n)) return 'Unknown';
    if (n >= 80) return 'Excellent';
    if (n >= 65) return 'Good';
    if (n >= 50) return 'Fair';
    if (n >= 35) return 'Needs work';
    return 'At risk';
  };

  // setRowMessage removed

  const fetchScoreForApplicant = useCallback(
    async (applicant, months) => {
      const tenantUserId = applicant?.tenantUserId;
      if (!tenantUserId) return;
      setScoreError('');
      setScoreLoading(true);
      setViewedScore(null);
      try {
        const res =
          variant === 'agent'
            ? await getTenantScoreByUserIdAsAgent(tenantUserId, months, { propertyId: applicant?.propertyId })
            : await getTenantScoreByUserId(tenantUserId, months);
        setViewedScore({
          ...res?.data,
          tenantName: applicant?.tenantName || 'Tenant',
          tenantEmail: applicant?.tenantEmail || '',
        });
        writeAccessStatus(variant, tenantUserId, 'granted');
      } catch (e) {
        const msg = e?.message || 'Failed to load tenant score.';
        setScoreError(msg);
        console.error('Fetch score error:', e);
      } finally {
        setScoreLoading(false);
      }
    },
    [variant]
  );

  useEffect(() => {
    if (!selectedApplicant) {
      setViewedScore(null);
      setScoreError('');
      setScoreLoading(false);
      return;
    }
    fetchScoreForApplicant(selectedApplicant, monthsBack);
  }, [selectedApplicant, monthsBack, fetchScoreForApplicant]);

  // Legacy effects removed

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingApplicants(true);
      setApplicantsError('');
      try {
        let propertyIds = [];
        if (variant === 'agent') {
          const assignmentsRes = await getMyAgentAssignments({ page: 0, size: 100 });
          const assignments = assignmentsRes?.data?.content || [];
          propertyIds = [...new Set((Array.isArray(assignments) ? assignments : []).map((a) => a?.propertyId).filter(Boolean))];
        } else if (variant === 'bnb') {
          const propertiesRes = await getMyProperties({ page: 0, size: 100, search: '', dashboardScope: 'BNB' });
          const properties = propertiesRes?.data?.content || [];
          propertyIds = (Array.isArray(properties) ? properties : []).map((p) => p?.id).filter(Boolean);
        } else {
          const propertiesRes = await getMyProperties({ page: 0, size: 100, search: '' });
          const properties = propertiesRes?.data?.content || [];
          propertyIds = (Array.isArray(properties) ? properties : []).map((p) => p?.id).filter(Boolean);
        }
        const requestResults = await Promise.all(
          propertyIds.map((propertyId) =>
            (
              variant === 'bnb'
                ? fetchRelevantBnbBookingsForProperty(propertyId)
                : Promise.all([
                    fetchActiveUnitRequestsForProperty(propertyId),
                    fetchManualTenanciesForProperty(propertyId),
                  ]).then(([unitRequests, manualTenancies]) => [...unitRequests, ...manualTenancies])
            )
              .then((requests) => ({ propertyId, requests }))
              .catch(() => ({ propertyId, requests: [] }))
          )
        );

        const allRequests = requestResults.flatMap(({ requests }) => requests);
        const applicantsByUser = new Map();
        allRequests.forEach((req) => {
          const isBnb = variant === 'bnb';
          const tenantUserId = getTenantUserIdFromUnitRequest(req);
          const idStr = String(tenantUserId || '').trim();
          if (!idStr || applicantsByUser.has(idStr)) return;
          const tenantName =
            (isBnb && (req?.guestName)) ||
            [req?.userFirstName, req?.userLastName].filter(Boolean).join(' ') ||
            [req?.requesterFirstName, req?.requesterLastName].filter(Boolean).join(' ') ||
            'Tenant';
          const tenantEmail = (isBnb && (req?.guestEmail)) || req?.requesterEmail || req?.userEmail || '';
          applicantsByUser.set(idStr, {
            tenantUserId: idStr,
            tenantName,
            tenantEmail,
            tenantCode: req?.requesterTenantCode || req?.tenantCode || req?.tenant_code || req?.guestCode || '',
            bookingCode: req?.bookingCode || req?.code || '',
            propertyId: req?.propertyId || req?.__sourcePropertyId || '',
            propertyName: req?.propertyName || req?.property_name || 'Property',
            unitName: req?.unitName || req?.unitNumber || 'Unit',
          });
        });

        if (!cancelled) {
          setUnitApplicants(Array.from(applicantsByUser.values()));
        }
      } catch (e) {
        if (!cancelled) {
          setApplicantsError(e?.message || 'Failed to load unit request applicants.');
          setUnitApplicants([]);
        }
      } finally {
        if (!cancelled) setLoadingApplicants(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [variant]);

  const handleOpenScoreModal = (applicant) => {
    setSelectedApplicant(applicant);
  };

  const closeScoreModal = useCallback(() => {
    setSelectedApplicant(null);
    setViewedScore(null);
    setScoreError('');
  }, []);

  const propertyOptions = Array.from(
    new Map(
      unitApplicants
        .map((a) => [a.propertyId || a.propertyName, { id: a.propertyId || a.propertyName, name: a.propertyName }])
        .filter(([id]) => Boolean(id))
    ).values()
  );

  const unitOptions = Array.from(
    new Map(
      unitApplicants
        .filter((a) => propertyFilter === 'all' || (a.propertyId || a.propertyName) === propertyFilter)
        .map((a) => [a.unitName, a.unitName])
        .filter(([name]) => Boolean(name))
    ).values()
  );

  const filteredApplicants = useMemo(() => {
    const code = String(tenantCodeFilter || '').trim().toLowerCase();
    const baseList = unitApplicants.filter((a) => {
      const propertyKey = a.propertyId || a.propertyName;
      const matchesProperty = propertyFilter === 'all' || propertyKey === propertyFilter;
      const matchesUnit = unitFilter === 'all' || a.unitName === unitFilter;
      return matchesProperty && matchesUnit;
    });
    if (!code) return baseList;
    return baseList.filter((a) => {
      const matchTenant = String(a.tenantCode || '').toLowerCase().includes(code);
      const matchBooking = variant === 'bnb' && String(a.bookingCode || '').toLowerCase().includes(code);
      return matchTenant || matchBooking;
    });
  }, [unitApplicants, tenantCodeFilter, propertyFilter, unitFilter, variant]);

  const applicantsForDisplay = filteredApplicants;

  useEffect(() => {
    if (!selectedApplicant) return;
    const stillVisible = applicantsForDisplay.some((a) => a.tenantUserId === selectedApplicant.tenantUserId);
    if (!stillVisible) {
      setSelectedApplicant(null);
      setViewedScore(null);
      setScoreError('');
    }
  }, [propertyFilter, unitFilter, tenantCodeFilter, selectedApplicant, applicantsForDisplay]);

  useEffect(() => {
    if (!selectedApplicant) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeScoreModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedApplicant, closeScoreModal]);

  const scoreModalOpen = Boolean(selectedApplicant);

  useEffect(() => {
    if (!scoreModalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [scoreModalOpen]);

  return (
    <div className="tenant-score-container">
      <CollapsibleTopPanel id="tenant-score-insights-top-panel" defaultOpenOnMobile={false}>
        <div className="tenant-score-header">
          <div>
            <h1 className="page-title">TenantScore Insights</h1>
            <p className="page-subtitle">Search and open TenantScore instantly for each applicant.</p>
          </div>
        </div>

        <div className="tenant-score-tabs-container">
          <div className="tenant-score-tabs">
            <button
              className={`tenant-score-tab ${activeTab === 'applicants' ? 'active' : ''}`}
              onClick={() => setActiveTab('applicants')}
            >
              Applicants
            </button>
            <button
              className={`tenant-score-tab ${activeTab === 'ratings' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('ratings');
                // If we don't have a filter, maybe just show latest?
              }}
            >
              Ratings History
            </button>
          </div>
          {activeTab === 'ratings' && ratingsTenantFilter && (
            <div className="tenant-score-active-filter">
              Showing ratings for: <strong>{ratingsTenantFilter.name}</strong>
              <button 
                className="tenant-score-clear-filter"
                onClick={() => setRatingsTenantFilter(null)}
              >
                Clear filter
              </button>
            </div>
          )}
        </div>

        <div className="tenant-score-filter-row">
          <div className="tenant-score-filter-group">
            <label htmlFor="tenant-score-code-filter" className="tenant-score-toolbar-label">
              Tenant code
            </label>
            <div className="tenant-score-code-search">
              <input
                id="tenant-score-code-filter"
                type="text"
                className="tenant-score-toolbar-select tenant-score-code-input"
                value={tenantCodeFilter}
                onChange={(e) => setTenantCodeFilter(e.target.value)}
                placeholder={variant === 'bnb' ? "Enter booking or tenant code" : "Enter tenant code"}
              />
            </div>
          </div>
          <div className="tenant-score-filter-group">
            <label htmlFor="tenant-score-property-filter" className="tenant-score-toolbar-label">
              Property
            </label>
            <select
              id="tenant-score-property-filter"
              className="tenant-score-toolbar-select"
              value={propertyFilter}
              onChange={(e) => {
                setPropertyFilter(e.target.value);
                setUnitFilter('all');
              }}
            >
              <option value="all">All properties</option>
              {propertyOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="tenant-score-filter-group">
            <label htmlFor="tenant-score-unit-filter" className="tenant-score-toolbar-label">
              Unit
            </label>
            <select
              id="tenant-score-unit-filter"
              className="tenant-score-toolbar-select"
              value={unitFilter}
              onChange={(e) => setUnitFilter(e.target.value)}
            >
              <option value="all">All units</option>
              {unitOptions.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      <div className="tenant-score-layout">
        {activeTab === 'applicants' ? (
          <div className="tenant-score-layout__list tenant-score-layout__list--full">
            <h3 className="card-title score-requests-section-title">Applicants</h3>
            {loadingApplicants ? (
              <div className="empty-state">Loading applicants…</div>
            ) : applicantsError ? (
              <div className="empty-state">{applicantsError}</div>
            ) : filteredApplicants.length === 0 ? (
              <div className="empty-state tenant-score-empty">
                <p className="tenant-score-empty__title">No TenantScore matches this filter</p>
                <p className="tenant-score-empty__text">
                  Try another tenant code, property, or unit filter to find the right tenant.
                </p>
              </div>
            ) : (
              <div className="score-requests-list">
                {applicantsForDisplay.map((applicant) => {
                  const id = applicant.tenantUserId;
                  const isSelected = selectedApplicant?.tenantUserId === id;
                  return (
                    <div
                      key={id}
                      className={`score-request-item ${isSelected ? 'score-request-item--selected' : ''}`}
                    >
                      <div className="score-request-main">
                        <div className="score-request-header-info">
                          <div className="score-request-name">{applicant.tenantName}</div>
                        </div>
                        <div className="score-request-meta">
                          {variant === 'bnb' && applicant.bookingCode && (
                            <span style={{ color: '#0474C4', fontWeight: 'bold' }}>Booking: {applicant.bookingCode}</span>
                          )}
                          <span>{applicant.tenantCode ? `Code: ${applicant.tenantCode}` : 'No tenant code'}</span>
                          <span>{applicant.propertyName}</span>
                          <span>{applicant.unitName}</span>
                        </div>
                      </div>
                      <div className="score-request-actions score-request-actions--full">
                        <button
                          type="button"
                          className="btn btn-primary btn-sm btn-full-width"
                          onClick={() => handleOpenScoreModal(applicant)}
                        >
                          View TenantScore
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="tenant-score-layout__ratings tenant-score-layout__list--full">
            <h3 className="card-title score-requests-section-title">
              {ratingsTenantFilter ? `Ratings History for ${ratingsTenantFilter.name}` : 'Ratings History'}
            </h3>
            
            {!ratingsTenantFilter ? (
              <div className="score-requests-list">
                {filteredApplicants.length === 0 ? (
                  <div className="empty-state tenant-score-empty">
                    <p className="tenant-score-empty__title">No applicants found</p>
                    <p className="tenant-score-empty__text">Try adjusting your search or filters.</p>
                  </div>
                ) : (
                  filteredApplicants.map((applicant) => {
                    const id = applicant.tenantUserId;
                    return (
                      <div key={id} className="score-request-item">
                        <div className="score-request-main">
                          <div className="score-request-name">{applicant.tenantName}</div>
                          <div className="score-request-meta">
                            {variant === 'bnb' && applicant.bookingCode && (
                              <span style={{ color: '#0474C4', fontWeight: 'bold' }}>Booking: {applicant.bookingCode}</span>
                            )}
                            <span>{applicant.tenantCode || 'No code'}</span>
                            <span>{applicant.propertyName}</span>
                          </div>
                        </div>
                        <div className="score-request-actions score-request-actions--full">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm btn-full-width"
                            onClick={async () => {
                              // Fetch score to get ratings if not already there
                              setScoreLoading(true);
                              setRatingsPage(1); // Reset to page 1
                              try {
                                const res = variant === 'agent'
                                  ? await getTenantScoreByUserIdAsAgent(applicant.tenantUserId, monthsBack, { propertyId: applicant.propertyId })
                                  : await getTenantScoreByUserId(applicant.tenantUserId, monthsBack);
                                setRatingsTenantFilter({
                                  userId: applicant.tenantUserId,
                                  name: applicant.tenantName,
                                  ratings: res?.data?.ratings || []
                                });
                              } catch (e) {
                                console.error('Failed to fetch ratings:', e);
                              } finally {
                                setScoreLoading(false);
                              }
                            }}
                          >
                            View Ratings History
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setRatingsTenantFilter(null)}
                  >
                    ← Back to All Applicants
                  </button>
                </div>
                <div className="ratings-full-view-grid">
                  {scoreLoading ? (
                    <div className="empty-state">Loading ratings history…</div>
                  ) : ratingsTenantFilter.ratings && ratingsTenantFilter.ratings.length > 0 ? (
                    <>
                      {ratingsTenantFilter.ratings
                        .slice((ratingsPage - 1) * ratingsPerPage, ratingsPage * ratingsPerPage)
                        .map((rating) => (
                          <div key={rating.id} className="rating-history-item rating-history-item--full">
                            <div className="rating-history-header">
                              <div className="rating-history-stars">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <FaStar
                                    key={star}
                                    className={`rating-star ${rating.score >= star ? 'active' : ''}`}
                                  />
                                ))}
                              </div>
                              <span className="rating-history-date">
                                {new Date(rating.createdAt).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                            <p className="rating-history-message">"{rating.message}"</p>
                            <div className="rating-history-footer">
                              <div className="rating-history-footer-group">
                                <span className="kv-label">Property</span>
                                <span className="rating-history-property">{rating.propertyName}</span>
                              </div>
                              <div className="rating-history-footer-group" style={{ textAlign: 'right' }}>
                                <span className="kv-label">Reviewed By</span>
                                <span className="rating-history-actor">{rating.actorName}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      
                      {ratingsTenantFilter.ratings.length > ratingsPerPage && (
                        <div className="tenant-score-pagination">
                          <button
                            className="pagination-btn"
                            disabled={ratingsPage === 1}
                            onClick={() => setRatingsPage(p => Math.max(1, p - 1))}
                          >
                            Previous
                          </button>
                          <span className="pagination-info">
                            Page {ratingsPage} of {Math.ceil(ratingsTenantFilter.ratings.length / ratingsPerPage)}
                          </span>
                          <button
                            className="pagination-btn"
                            disabled={ratingsPage >= Math.ceil(ratingsTenantFilter.ratings.length / ratingsPerPage)}
                            onClick={() => setRatingsPage(p => p + 1)}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">No ratings found for this tenant.</div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {scoreModalOpen &&
        typeof document !== 'undefined' &&
        document.body &&
        createPortal(
          <div
            className="modal-overlay tenant-score-modal-overlay"
            role="presentation"
            onClick={closeScoreModal}
          >
            <div
              className="modal-content tenant-score-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="tenant-score-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header tenant-score-modal__header">
                <div>
                  <h2 id="tenant-score-modal-title">Tenant score</h2>
                  <p className="tenant-score-modal__tenant-name">{selectedApplicant.tenantName}</p>
                  <p className="tenant-score-modal__tenant-meta">
                    {selectedApplicant.tenantEmail || '—'}
                    <span className="tenant-score-modal__dot"> · </span>
                    {selectedApplicant.propertyName}
                    <span className="tenant-score-modal__dot"> · </span>
                    {selectedApplicant.unitName}
                  </p>
                </div>
                <button type="button" className="close-btn" onClick={closeScoreModal} aria-label="Close">
                  ×
                </button>
              </div>

              <div className="tenant-score-modal__body">
                <div className="tenant-score-toolbar tenant-score-modal__toolbar">
                  <label htmlFor="tenant-score-months-modal" className="tenant-score-toolbar-label">
                    Period
                  </label>
                  <select
                    id="tenant-score-months-modal"
                    className="tenant-score-toolbar-select"
                    value={monthsBack}
                    onChange={(e) => setMonthsBack(Number(e.target.value))}
                  >
                    <option value={3}>Last 3 months</option>
                    <option value={6}>Last 6 months</option>
                    <option value={12}>Last 12 months</option>
                    <option value={24}>Last 24 months</option>
                  </select>
                </div>

                {scoreLoading ? (
                  <p className="tenant-score-panel-loading">Loading score…</p>
                ) : scoreError ? (
                  <p className="tenant-score-view-card__error">{scoreError}</p>
                ) : viewedScore ? (
                  <>
                    <div className="tenant-score-hero tenant-score-hero--modal">
                      <div className="tenant-score-hero__value">{Number(viewedScore.tenantScore || 0).toFixed(1)}</div>
                      <div className="tenant-score-hero__band">{getScoreBand(viewedScore.tenantScore)}</div>
                      <div className="tenant-score-hero__period">{formatPeriod(viewedScore)}</div>
                    </div>
                    <div className="tenant-score-view-card__grid tenant-score-modal__grid">
                      <div>
                        <span className="kv-label">Months counted</span>
                        <span className="kv-value">{viewedScore.totalMonthsCounted ?? '—'}</span>
                      </div>
                      <div>
                        <span className="kv-label">On-time</span>
                        <span className="kv-value">{viewedScore.onTimeCount ?? 0}</span>
                      </div>
                      <div>
                        <span className="kv-label">Late</span>
                        <span className="kv-value">{viewedScore.lateCount ?? 0}</span>
                      </div>
                      <div>
                        <span className="kv-label">Missed</span>
                        <span className="kv-value">{viewedScore.missedCount ?? 0}</span>
                      </div>
                    </div>

                    {viewedScore.ratings && viewedScore.ratings.length > 0 && (
                      <div className="tenant-score-ratings-section">
                        <div className="ratings-section-header">
                          <h4 className="ratings-section-title">Recent Feedback</h4>
                          {viewedScore.ratings.length > 2 && (
                            <button 
                              className="view-all-ratings-btn"
                              onClick={() => {
                                setRatingsTenantFilter({
                                  userId: viewedScore.tenantUserId,
                                  name: viewedScore.tenantName,
                                  ratings: viewedScore.ratings
                                });
                                setActiveTab('ratings');
                                closeScoreModal();
                              }}
                            >
                              View all {viewedScore.ratings.length} ratings
                            </button>
                          )}
                        </div>
                        <div className="ratings-history-list">
                          {viewedScore.ratings.slice(0, 2).map((rating) => (
                            <div key={rating.id} className="rating-history-item">
                              <div className="rating-history-header">
                                <div className="rating-history-stars">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <FaStar
                                      key={star}
                                      className={`rating-star ${rating.score >= star ? 'active' : ''}`}
                                    />
                                  ))}
                                </div>
                                <span className="rating-history-date">
                                  {new Date(rating.createdAt).toLocaleDateString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}
                                </span>
                              </div>
                              <p className="rating-history-message">"{rating.message}"</p>
                              <div className="rating-history-footer">
                                <span className="rating-history-property">{rating.propertyName}</span>
                                <span className="rating-history-actor">Rated by {rating.actorName}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TenantScore;
